'use strict';

var React = require('react');
var reactUtils = require('@sravni/react-utils');
var getCurrentStep = require('../../../helpers/getCurrentStep.js');
var isRange = require('../../../utils/isRange.js');
require('../../Typography/src/Heading.js');
require('../../Typography/src/Link.js');
require('../../Typography/src/ListItem.js');
require('../../Typography/src/OrderedList.js');
var Text = require('../../Typography/src/Text.js');
require('../../Typography/src/UnorderedList.js');
var Slider_module = require('../styles/Slider.module.less.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable @typescript-eslint/no-use-before-define */
const getLogarithmicValue = (min, max, position) => {
    // position will be between 0 and 100
    const minp = 0;
    const maxp = 100;
    // The result should be between min anв max
    const minv = Math.log(min || 1);
    const maxv = Math.log(max);
    // calculate adjustment factor
    const scale = (maxv - minv) / (maxp - minp);
    return Math.exp((position - minp) * scale + minv);
};
const getLogarithmicPosition = (min, max, value) => {
    // position will be between 0 and 100
    const minp = 0;
    const maxp = 100;
    // The result should be between min and max
    const minv = Math.log(min || 1);
    const maxv = Math.log(max);
    // calculate adjustment factor
    const scale = (maxv - minv) / (maxp - minp);
    return minp + (Math.log(value) - minv) / scale;
};
const getPrecision = (value) => {
    if (value > 5000000)
        return 100000;
    if (value > 1000000)
        return 50000;
    if (value > 100000)
        return 10000;
    if (value > 1000)
        return 100;
    // if (value > 500) return 10;
    return 1;
};
const roundValue = (value) => {
    const precision = getPrecision(value);
    return Math.round(value / precision) * precision;
};
const correctNumberByRange = (min, max, value) => Math.min(max, Math.max(value, min));
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const addEventsToDocument = (eventMap) => {
    for (const key in eventMap) {
        document.addEventListener(key, eventMap[key], false);
    }
};
const removeEventsFromDocument = (eventMap) => {
    for (const key in eventMap) {
        document.removeEventListener(key, eventMap[key], false);
    }
};
const getMousePosition = (event) => event.pageX - (window.scrollX || window.pageXOffset);
const getTouchPosition = (event) => event.touches[0].pageX - (window.scrollX || window.pageXOffset);
const pauseEvent = (event) => {
    event.stopPropagation();
    event.preventDefault();
};
const isToValue = (value, newValue) => {
    const closestValue = [value.from, value.to].sort((a = 0, b = 0) => Math.abs(newValue - a) - Math.abs(newValue - b))[0];
    return closestValue === value.to;
};
/**
 * @description A Slider component for displaying current value and intervals in range
 *
 * @component
 * @example
 * ```jsx
 * <Slider min={1000} max={100000} step={1} value={value} onChange={handleChange} />
 * ```
 */
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line max-statements
const Slider = React__namespace.forwardRef((props, ref) => {
    // eslint-disable-next-line no-unused-vars
    const { value = 0, min = 0, max = 100, step = 1, showValue, disabled, logarithmic, mode = 'single', onChange, className, ...restProps } = props;
    const sliderRef = React__namespace.useRef(null);
    const [inProgress, setInProgress] = reactUtils.useBoolean(false);
    const cx = reactUtils.useStyles(Slider_module.default);
    const isRangeMode = mode === 'range';
    if (value && ((isRangeMode && !isRange.isRange(value)) || (!isRangeMode && isRange.isRange(value)))) {
        reactUtils.error({
            condition: true,
            message: 'Ensure value matches the mode',
        })();
    }
    const classNamesInner = cx('inner', {
        disabled,
    });
    const classNamesTrack = cx('track', {
        range: isRangeMode,
        disabled,
    });
    const classNamesHandlerWrapper = cx('handler-wrapper', {
        active: inProgress,
    });
    const classNamesHandler = cx('handler', {
        disabled,
    });
    const classNamesCaption = cx('caption', {
        disabled,
    });
    const handleMouseDown = (event) => {
        if (disabled) {
            return;
        }
        let isTo = false;
        if (isRange.isRange(value)) {
            const newValue = positionToValue(getMousePosition(event));
            isTo = isToValue(value, newValue);
        }
        addEventsToDocument(getMouseEventMap(isTo));
        start(getMousePosition(event));
        pauseEvent(event);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleMouseUp = () => {
        end(getMouseEventMap());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleMouseUpTo = () => {
        end(getMouseEventMap(true));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleMouseMove = (event) => {
        pauseEvent(event);
        move(getMousePosition(event));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleMouseMoveTo = (event) => {
        pauseEvent(event);
        move(getMousePosition(event), true);
    };
    const handleTouchStart = (event) => {
        if (disabled) {
            return;
        }
        let isTo = false;
        if (isRange.isRange(value)) {
            const newValue = positionToValue(getTouchPosition(event));
            isTo = isToValue(value, newValue);
        }
        start(getTouchPosition(event));
        addEventsToDocument(getTouchEventMap(isTo));
        pauseEvent(event);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleTouchMove = (event) => {
        move(getTouchPosition(event));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleTouchMoveTo = (event) => {
        move(getTouchPosition(event), true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleTouchEnd = () => {
        end(getTouchEventMap());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleTouchEndTo = () => {
        end(getTouchEventMap(true));
    };
    const handleChange = React__namespace.useCallback((newValue, isToValue) => {
        if (!isRangeMode) {
            onChange?.(newValue);
            return;
        }
        const innerValue = isRange.isRange(value) ? { ...value } : { from: min, to: max };
        innerValue[isToValue ? 'to' : 'from'] = newValue;
        if (!innerValue.from) {
            innerValue.from = min;
            onChange?.(innerValue);
            return;
        }
        if (!innerValue.to || (innerValue.to && innerValue.from > innerValue.to)) {
            const oldFrom = innerValue.from;
            innerValue.from = innerValue.to ?? min;
            innerValue.to = oldFrom;
        }
        onChange?.(innerValue);
    }, [value, min, isRangeMode, max, onChange]);
    const getSliderBound = () => {
        const slider = sliderRef.current;
        return slider ? slider.clientWidth : 1;
    };
    const getSliderStart = () => {
        const slider = sliderRef.current;
        return slider ? slider.getBoundingClientRect().left : 0;
    };
    const start = (position) => {
        setInProgress.on();
        const newValue = positionToValue(position);
        let isTo = false;
        if (isRangeMode) {
            isTo = isToValue(isRange.isRange(value) ? { ...value } : { from: min, to: max }, newValue);
        }
        handleChange(newValue, isTo);
    };
    const end = (eventMap) => {
        setInProgress.off();
        removeEventsFromDocument(eventMap);
    };
    const move = (position, isToValue) => {
        handleChange(positionToValue(position), isToValue);
    };
    const getOffset = (isToValue) => {
        if (isRangeMode && !isRange.isRange(value)) {
            return isToValue ? 100 : 0;
        }
        const innerValue = isRange.isRange(value) ? value[isToValue ? 'to' : 'from'] || 0 : value;
        let percent = ((innerValue - min) / (max - min)) * 100;
        if (logarithmic) {
            percent = getLogarithmicPosition(min, max, innerValue);
        }
        if (max === min) {
            percent = 100;
        }
        if (Number.isNaN(percent)) {
            percent = 0;
        }
        return correctNumberByRange(0, 100, percent);
    };
    const positionToValue = (position) => {
        const offset = calcOffsetFromPosition(position);
        const value = calcValue(offset);
        const currentStep = getCurrentStep.getCurrentStep(step, value);
        const valModStep = (value - min) % currentStep;
        let alignedValue = value - valModStep;
        if (Math.abs(valModStep) * 2 >= currentStep) {
            alignedValue += valModStep > 0 ? currentStep : -currentStep;
        }
        alignedValue = parseFloat(alignedValue.toFixed(5));
        return correctNumberByRange(min, max, alignedValue);
    };
    const calcOffsetFromPosition = (position) => position - getSliderStart();
    const calcValue = (offset) => {
        const ratio = offset / getSliderBound();
        const value = logarithmic ? getLogarithmicValue(min, max, ratio * 100) : ratio * (max - min) + min;
        const currentStep = getCurrentStep.getCurrentStep(step, value);
        // round value if step doesnt present
        return currentStep > 1 ? value : roundValue(value);
    };
    const getMouseEventMap = React__namespace.useCallback((isToValue) => ({
        mousemove: isToValue ? handleMouseMoveTo : handleMouseMove,
        mouseup: isToValue ? handleMouseUpTo : handleMouseUp,
    }), [handleMouseMove, handleMouseMoveTo, handleMouseUp, handleMouseUpTo]);
    const getTouchEventMap = React__namespace.useCallback((isToValue) => ({
        touchmove: isToValue ? handleTouchMoveTo : handleTouchMove,
        touchend: isToValue ? handleTouchEndTo : handleTouchEnd,
    }), [handleTouchEnd, handleTouchEndTo, handleTouchMove, handleTouchMoveTo]);
    React__namespace.useEffect(() => () => {
        removeEventsFromDocument(getMouseEventMap());
        removeEventsFromDocument(getMouseEventMap(true));
        removeEventsFromDocument(getTouchEventMap());
        removeEventsFromDocument(getTouchEventMap(true));
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const offset = getOffset();
    const offsetTo = getOffset(true);
    const handlerStyles = { marginLeft: `${offset}%` };
    const handlerStylesTo = { marginLeft: `${offsetTo}%` };
    const trackStyles = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '--start': `${offset <= offsetTo ? offset : offsetTo}%`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '--range': `${offsetTo > offset ? offsetTo : offset}%`,
    };
    return (React__namespace.createElement("div", { ref: ref, "data-qa": "Slider", className: className, ...restProps },
        React__namespace.createElement("div", { ref: sliderRef, className: classNamesInner, onMouseDown: handleMouseDown, onTouchStart: handleTouchStart },
            React__namespace.createElement("div", { style: trackStyles, className: classNamesTrack }),
            React__namespace.createElement("div", { style: handlerStyles, className: classNamesHandlerWrapper },
                React__namespace.createElement("span", { className: classNamesHandler, onMouseDown: handleMouseDown, onTouchStart: handleTouchStart }),
                showValue && (React__namespace.createElement(Text.Text, { className: classNamesCaption, size: 10, strong: true }, isRange.isRange(value) ? value.from : value))),
            isRangeMode && (React__namespace.createElement("div", { style: handlerStylesTo, className: classNamesHandlerWrapper },
                React__namespace.createElement("span", { className: classNamesHandler, onMouseDown: handleMouseDown, onTouchStart: handleTouchStart }),
                showValue && (React__namespace.createElement(Text.Text, { className: classNamesCaption, size: 10, strong: true }, isRange.isRange(value) ? value.to : '')))))));
});
Slider.defaultProps = {
    min: 0,
    max: 100,
    step: 1,
    showValue: false,
    mode: 'single',
};
Slider.displayName = 'Slider';

exports.Slider = Slider;
//# sourceMappingURL=Slider.js.map
